import type { SearchMode } from "agent-twitter-client";
import { promises as fs } from "fs";
import type { Tweet } from "@podx/core/types";
import { CryptoAnalyzer } from "../crypto/index.js";
import { BaseScraper } from "./baseScraper";

export interface SearchOptions {
  query: string;
  maxTweets: number;
  searchMode: 'Latest' | 'Top' | 'People' | 'Photos' | 'Videos';
  includeRetweets?: boolean;
  minLikes?: number;
  minRetweets?: number;
  dateFrom?: string;
  dateTo?: string;
}

export interface ListOptions {
  listId: string;
  maxTweets: number;
  includeRetweets?: boolean;
}

export class SearchScraper extends BaseScraper {
  private cryptoAnalyzer: any;

  constructor() {
    super();
    this.cryptoAnalyzer = new CryptoAnalyzer();
  }

  // Convert our SearchMode string to the library's SearchMode enum
  private getSearchMode(mode: string): any {
    // Import SearchMode dynamically to avoid namespace issues
    const { SearchMode } = require("agent-twitter-client");
    switch (mode.toLowerCase()) {
      case 'latest':
        return SearchMode.Latest;
      case 'top':
        return SearchMode.Top;
      default:
        return SearchMode.Latest;
    }
  }

  // Build advanced search query with filters
  private buildSearchQuery(options: SearchOptions): string {
    let query = options.query;

    // Add date filters
    if (options.dateFrom) {
      query += ` since:${options.dateFrom}`;
    }
    if (options.dateTo) {
      query += ` until:${options.dateTo}`;
    }

    // Add engagement filters - only add if explicitly set and > 0
    if (options.minLikes && options.minLikes > 0) {
      query += ` min_faves:${options.minLikes}`;
    }
    if (options.minRetweets && options.minRetweets > 0) {
      query += ` min_retweets:${options.minRetweets}`;
    }

    // Exclude retweets if specified
    if (!options.includeRetweets) {
      query += ` -is:retweet`;
    }

    return query;
  }

  // Search tweets with advanced filtering
  async searchTweets(options: SearchOptions): Promise<Tweet[]> {
    try {
      // Initialize scraper if not already done
      await this.initializeScraper();
      
      console.log(`🔍 Searching for: "${options.query}"`);
      console.log(`📊 Mode: ${options.searchMode}, Max: ${options.maxTweets}`);

      const searchQuery = this.buildSearchQuery(options);
      const searchMode = this.getSearchMode(options.searchMode);

      console.log(`🔎 Final query: "${searchQuery}"`);

      const tweets: Tweet[] = [];
      const tweetGenerator = this.scraper!.searchTweets(searchQuery, options.maxTweets, searchMode);

      for await (const tweet of (tweetGenerator as any)) {
        const processedTweet = this.processTweet(tweet);
        if (processedTweet) {
          tweets.push(processedTweet);
        }

        if (tweets.length >= options.maxTweets) break;
      }

      console.log(`✅ Found ${tweets.length} tweets for query: "${options.query}"`);
      return tweets;

    } catch (error) {
      console.error(`❌ Search failed for query "${options.query}":`, error);
      return [];
    }
  }

  // Scrape tweets from a Twitter list
  async scrapeList(options: ListOptions): Promise<Tweet[]> {
    try {
      // Initialize scraper if not already done
      await this.initializeScraper();
      
      console.log(`📋 Scraping tweets from list: ${options.listId}`);

      const tweets: Tweet[] = [];
      const listTweets = await (this.scraper as any)!.fetchListTweets(options.listId, options.maxTweets);

      for await (const tweet of (listTweets as any)) {
        // Skip retweets if not requested
        if (!options.includeRetweets && tweet.isRetweet) {
          continue;
        }

        const processedTweet = this.processTweet(tweet);
        if (processedTweet) {
          tweets.push(processedTweet);
        }

        if (tweets.length >= options.maxTweets) break;
      }

      console.log(`✅ Scraped ${tweets.length} tweets from list ${options.listId}`);
      return tweets;

    } catch (error) {
      console.error(`❌ List scraping failed for list ${options.listId}:`, error);
      return [];
    }
  }

  // Process and normalize tweet data
  private processTweet(rawTweet: any): Tweet | null {
    try {
      const text = rawTweet.text || rawTweet.full_text || '';
      const tweet: Tweet = {
        id: rawTweet.id || rawTweet.id_str,
        text,
        createdAt: rawTweet.createdAt || rawTweet.created_at || new Date().toISOString(),
        retweetCount: rawTweet.retweets || rawTweet.retweetCount || rawTweet.retweet_count || 0,
        likeCount: rawTweet.likes || rawTweet.likeCount || rawTweet.favorite_count || 0,
        replies: rawTweet.replies || 0,
        username: rawTweet.username || rawTweet.user?.screen_name,
        name: rawTweet.name || rawTweet.user?.name,
        conversationId: rawTweet.conversationId,
        hashtags: this.extractHashtags(text),
        mentions: this.extractMentions(text)
      };

      return tweet;
    } catch (error) {
      console.error('❌ Failed to process tweet:', error);
      return null;
    }
  }

  // Extract hashtags from tweet text
  private extractHashtags(text: string): string[] {
    const hashtagRegex = /#(\w+)/g;
    const matches = text.match(hashtagRegex) || [];
    return matches.map(hashtag => hashtag.substring(1)); // Remove the #
  }

  // Extract mentions from tweet text
  private extractMentions(text: string): string[] {
    const mentionRegex = /@(\w+)/g;
    const matches = text.match(mentionRegex) || [];
    return matches.map(mention => mention.substring(1)); // Remove the @
  }

  // Enhanced search for specific hashtags
  async searchHashtags(hashtags: string[], maxTweets: number = 100): Promise<Tweet[]> {
    try {
      console.log(`🏷️ Searching for hashtags: ${hashtags.join(', ')}`);
      await this.initializeScraper();
      
      const allTweets: Tweet[] = [];
      const maxTweetsPerHashtag = Math.floor(maxTweets / hashtags.length);
      
      for (const hashtag of hashtags) {
        const cleanHashtag = hashtag.startsWith('#') ? hashtag : `#${hashtag}`;
        const searchOptions = {
          query: cleanHashtag,
          maxTweets: maxTweetsPerHashtag,
          searchMode: 'Latest' as any,
          includeRetweets: true
        };
        
        console.log(`🔍 Searching hashtag: ${cleanHashtag}`);
        const tweets = await this.searchTweets(searchOptions);
        allTweets.push(...tweets);
      }
      
      console.log(`✅ Found ${allTweets.length} tweets with hashtags`);
      return allTweets;
      
    } catch (error) {
      console.error('❌ Hashtag search failed:', error);
      return [];
    }
  }

  // Enhanced search for specific ticker symbols
  async searchTickers(tickers: string[], maxTweets: number = 100): Promise<Tweet[]> {
    try {
      console.log(`💰 Searching for tickers: ${tickers.join(', ')}`);
      await this.initializeScraper();
      
      const allTweets: Tweet[] = [];
      const maxTweetsPerTicker = Math.floor(maxTweets / tickers.length);
      
      for (const ticker of tickers) {
        const cleanTicker = ticker.startsWith('$') ? ticker : `$${ticker}`;
        const searchOptions = {
          query: cleanTicker,
          maxTweets: maxTweetsPerTicker,
          searchMode: 'Latest' as any,
          includeRetweets: true
        };
        
        console.log(`🔍 Searching ticker: ${cleanTicker}`);
        const tweets = await this.searchTweets(searchOptions);
        allTweets.push(...tweets);
      }
      
      console.log(`✅ Found ${allTweets.length} tweets with tickers`);
      return allTweets;
      
    } catch (error) {
      console.error('❌ Ticker search failed:', error);
      return [];
    }
  }

  // Search replies for specific tweets
  async searchReplies(tweetIds: string[], maxRepliesPerTweet: number = 50): Promise<{
    replies: any[];
    byTweet: Map<string, any[]>;
  }> {
    try {
      console.log(`💬 Searching replies for ${tweetIds.length} tweets`);
      await this.initializeScraper();
      
      const allReplies: any[] = [];
      const repliesByTweet = new Map<string, any[]>();
      
      for (const tweetId of tweetIds) {
        console.log(`🔍 Fetching replies for tweet: ${tweetId}`);
        
        try {
          const replies: any[] = [];
          const replyGenerator = await (this.scraper as any).getTweetsAndReplies(tweetId, maxRepliesPerTweet);
          
          for await (const reply of replyGenerator) {
            const processedReply = this.processTweet(reply);
            if (processedReply) {
              replies.push(processedReply);
            }
            if (replies.length >= maxRepliesPerTweet) break;
          }
          
          console.log(`✅ Found ${replies.length} replies for tweet ${tweetId}`);
          allReplies.push(...replies);
          repliesByTweet.set(tweetId, replies);
          
        } catch (error) {
          console.error(`❌ Failed to fetch replies for tweet ${tweetId}:`, error);
          repliesByTweet.set(tweetId, []);
        }
        
        // Small delay between requests
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      console.log(`✅ Total replies found: ${allReplies.length}`);
      return { replies: allReplies, byTweet: repliesByTweet };
      
    } catch (error) {
      console.error('❌ Reply search failed:', error);
      return { replies: [], byTweet: new Map() };
    }
  }

  // Search for crypto-specific content
  async searchCrypto(options: {
    tokens?: string[];
    keywords?: string[];
    maxTweets: number;
    searchMode?: 'Latest' | 'Top';
    minEngagement?: number;
  }): Promise<{
    tweets: Tweet[];
    solanaContracts: any;
    tokenMentions: any;
  }> {
    const queries: string[] = [];

    // Build crypto-specific queries
    if (options.tokens) {
      options.tokens.forEach(token => {
        queries.push(`$${token.toUpperCase()}`);
      });
    }

    if (options.keywords) {
      queries.push(...options.keywords);
    }

    // Default crypto keywords if none provided
    if (queries.length === 0) {
      queries.push('$SOL', '$BTC', '$ETH', 'pump.fun', 'solana', 'crypto', 'defi');
    }

    const allTweets: Tweet[] = [];

    // Search for each query
    for (const query of queries) {
      const searchOptions: SearchOptions = {
        query,
        maxTweets: Math.ceil(options.maxTweets / queries.length),
        searchMode: options.searchMode || 'Latest',
        includeRetweets: false
      };
      if (typeof options.minEngagement === 'number') searchOptions.minLikes = options.minEngagement;

      const tweets = await this.searchTweets(searchOptions);
      allTweets.push(...tweets);
    }

    // Remove duplicates
    const uniqueTweets = allTweets.filter((tweet, index, self) => 
      index === self.findIndex(t => t.id === tweet.id)
    );

    // Analyze for crypto content
    this.cryptoAnalyzer.tweets = uniqueTweets;
    const tokenMentions = this.cryptoAnalyzer.extractTokenMentions();
    const solanaContracts = this.cryptoAnalyzer.analyzeSolanaContracts();

    return {
      tweets: uniqueTweets.slice(0, options.maxTweets),
      solanaContracts,
      tokenMentions
    };
  }

  // Advanced search with multiple criteria
  async advancedSearch(options: {
    queries: string[];
    users?: string[];
    hashtags?: string[];
    mentions?: string[];
    maxTweetsPerQuery: number;
    searchMode?: 'Latest' | 'Top';
    dateRange?: { from: string; to: string };
    engagement?: { minLikes?: number; minRetweets?: number };
  }): Promise<Tweet[]> {
    const allTweets: Tweet[] = [];

    // Build complex queries
    const searchQueries: string[] = [];

    // Add base queries
    searchQueries.push(...options.queries);

    // Add user-specific searches
    if (options.users) {
      options.users.forEach(user => {
        searchQueries.push(`from:${user}`);
      });
    }

    // Add hashtag searches  
    if (options.hashtags) {
      options.hashtags.forEach(hashtag => {
        searchQueries.push(`#${hashtag}`);
      });
    }

    // Add mention searches
    if (options.mentions) {
      options.mentions.forEach(mention => {
        searchQueries.push(`@${mention}`);
      });
    }

    // Execute searches
    for (const query of searchQueries) {
      const searchOptions: SearchOptions = {
        query,
        maxTweets: options.maxTweetsPerQuery,
        searchMode: options.searchMode || 'Latest',
        includeRetweets: false
      };
      if (options.dateRange?.from) searchOptions.dateFrom = options.dateRange.from;
      if (options.dateRange?.to) searchOptions.dateTo = options.dateRange.to;
      if (typeof options.engagement?.minLikes === 'number') searchOptions.minLikes = options.engagement.minLikes;
      if (typeof options.engagement?.minRetweets === 'number') searchOptions.minRetweets = options.engagement.minRetweets;

      const tweets = await this.searchTweets(searchOptions);
      allTweets.push(...tweets);

      // Small delay between queries to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    // Remove duplicates and sort by engagement
    const uniqueTweets = allTweets
      .filter((tweet, index, self) => 
        index === self.findIndex(t => t.id === tweet.id)
      )
      .sort((a, b) => (b.likeCount + b.retweetCount) - (a.likeCount + a.retweetCount));

    return uniqueTweets;
  }

  // Export search results to file
  async exportResults(
    tweets: Tweet[], 
    filename: string, 
    metadata?: {
      query?: string;
      searchMode?: string;
      totalFound?: number;
      solanaContracts?: any;
      tokenMentions?: any;
    }
  ): Promise<void> {
    const exportData = {
      generatedAt: new Date().toISOString(),
      metadata: metadata || {},
      totalTweets: tweets.length,
      tweets
    };

    await fs.writeFile(filename, JSON.stringify(exportData, null, 2));
    console.log(`💾 Search results exported to ${filename}`);
  }
}
